import React, { useEffect, useState } from "react";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import { useNewsletterApi } from "scmp-sdk/components/newsletter/newsletter-widget/hooks";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import type { NewsletterPackageDetail } from "shared/data/newsletter/index";
import { NEWSLETTER_PACKAGES } from "shared/data/newsletter/index";
import { notEmpty } from "shared/lib/utils";

import {
  BoldNewsletterName,
  Description,
  DescriptionBodyText,
  IncludingText,
  RegularNewsletterName,
} from "./styles";

const reorderNewsletterItems = (
  newsletterItems: NewsletterItem[],
  packageDetail: NewsletterPackageDetail,
): NewsletterItem[] => {
  const orderMap = packageDetail.childNewsletterIds.reduce(
    (map, entityId, index) => {
      map[entityId] = index;
      return map;
    },
    {} as Record<string, number>,
  );

  return newsletterItems
    .filter(item => orderMap.hasOwnProperty(item.entityId))
    .sort((a, b) => {
      const aIndex = orderMap[a.entityId];
      const bIndex = orderMap[b.entityId];
      return aIndex - bIndex;
    });
};

export const DescriptionDisplay: React.FunctionComponent = () => {
  const { packageKey } = useNewsletterContext();
  const { fetchNewsletterByIds } = useNewsletterApi();
  const [allChildNewsletterItems, setAllChildNewsletterItems] = useState<NewsletterItem[]>([]);
  const isPicturesFilmsVideo = packageKey === "Pictures, Films & Video";

  useEffect(() => {
    const run = async () => {
      if (!packageKey) {
        setAllChildNewsletterItems([]);
        return;
      }

      const packageDetail = NEWSLETTER_PACKAGES[packageKey];
      if (!packageDetail || packageDetail.childNewsletterIds.length === 0) {
        setAllChildNewsletterItems([]);
        return;
      }

      try {
        const data = await fetchNewsletterByIds(
          packageDetail.childNewsletterIds,
          packageDetail.childNewsletterIds.length,
        );
        const orderedItems = reorderNewsletterItems(data?.filter(notEmpty) ?? [], packageDetail);

        setAllChildNewsletterItems(orderedItems);
      } catch {
        setAllChildNewsletterItems([]);
      }
    };

    void run();
  }, [packageKey, fetchNewsletterByIds]);

  return (
    <Description>
      {isPicturesFilmsVideo ? (
        <DescriptionBodyText>
          Our best videos, pictures, graphics and political cartoons of the week.
        </DescriptionBodyText>
      ) : (
        <>
          <IncludingText>including:</IncludingText>
          <DescriptionBodyText>
            {allChildNewsletterItems.length > 0 ? (
              <>
                <BoldNewsletterName>{allChildNewsletterItems[0].name}</BoldNewsletterName>
                {allChildNewsletterItems.length > 1 && (
                  <>
                    {" "}
                    /{" "}
                    <RegularNewsletterName>
                      {allChildNewsletterItems
                        .slice(1)
                        .map(item => item.name)
                        .join(" / ")}
                    </RegularNewsletterName>
                  </>
                )}
              </>
            ) : null}
          </DescriptionBodyText>
        </>
      )}
    </Description>
  );
};

DescriptionDisplay.displayName = "DescriptionDisplay";
